import { useState } from 'react';
import { ArchiveRestore, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import type { ArchivedInvestorSummary } from '@/services/investorService';
import { LW_THEME_CLASS } from './shared/InvestorThemeScope';

interface ArchivedInvestorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investors: ArchivedInvestorSummary[];
  loading: boolean;
  onRestore: (investorId: string) => Promise<void>;
}

export function ArchivedInvestorsDialog({
  open,
  onOpenChange,
  investors,
  loading,
  onRestore
}: ArchivedInvestorsDialogProps) {
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async (investor: ArchivedInvestorSummary) => {
    setRestoringId(investor.id);
    setError(null);

    try {
      await onRestore(investor.id);
    } catch (restoreError) {
      setError(restoreError instanceof Error ? restoreError.message : 'The investor could not be restored');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${LW_THEME_CLASS} sm:max-w-[620px]`}>
        <DialogHeader>
          <DialogTitle>Archived investors</DialogTitle>
          <DialogDescription>
            Archived investors stay linked to documents, payouts, and reconciliation history. Restore one to return it to active lists.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {loading && (
            <div role="status" className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading archived investors…
            </div>
          )}

          {!loading && investors.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">No archived investors.</p>
          )}

          {!loading && investors.map((investor) => (
            <div key={investor.id} className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">{investor.investorName}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Archived {new Date(investor.archivedAt).toLocaleString()}
                </p>
              </div>
              <button
                type="button"
                className="lw-btn lw-btn-secondary sm"
                disabled={restoringId !== null}
                onClick={() => handleRestore(investor)}
              >
                {restoringId === investor.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ArchiveRestore className="h-4 w-4" aria-hidden="true" />
                )}
                {restoringId === investor.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
